import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import { queryKeys } from '../queryKeys';

/**
 * Dashboard column visibility policy — which WmsTable columns are admin-only.
 *
 * Shape: tableKey → columnId → adminOnly.
 *   true  = admin-only (hidden from non-admins)
 *   false = explicitly public (overrides a code-level `meta.adminOnly` default)
 *   absent = fall back to the column's code default
 *
 * Read by every authenticated user (WmsTable gates columns with it); written
 * only by admins from /settings/dashboard-columns. UI-level visibility, not a
 * security boundary.
 */
export type TableColumnPolicy = Record<string, Record<string, boolean>>;

export function useTableColumnPolicy() {
  return useQuery({
    queryKey: queryKeys.tableColumnPolicies.all,
    queryFn: async () => {
      const { data } = await apiClient.get<{ policy: TableColumnPolicy }>(
        '/api/table-column-policies',
      );
      return data.policy;
    },
    // The policy changes rarely (admin action) — cache aggressively.
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });
}

/** Full-replace update (admin only). */
export function useUpdateTableColumnPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (policy: TableColumnPolicy) => {
      const { data } = await apiClient.put<{ policy: TableColumnPolicy }>(
        '/api/table-column-policies',
        { policy },
      );
      return data.policy;
    },
    onSuccess: (policy) => {
      queryClient.setQueryData(queryKeys.tableColumnPolicies.all, policy);
    },
  });
}
